import { WorkspaceTreeItem } from "features/data-management/workspace/WorkspaceManagementSlice"
import { Folder, TableMember } from "./types"
import { ContextMenuItem } from "./TODO-put-in-package/ContextMenu"
import {
    CreateFolder,
    MoveTo,
    OpenInNew,
    Pencil,
    PersonAdd,
    Star,
    StarFill,
    Trash,
} from "@nova/component-icons"
import { AddOns, Licenses, SubscriptionsType, ID } from "@nova/constants"
import { MemberExtended, WorkspaceMember } from "@nova/schemas"
import { DiscriminatedUnion } from "@nova/util-ts"

export const getSeatCounts = (members: MemberExtended[]) => {
    const result: Record<SubscriptionsType, number> = {
        lite: 0,
        essentials: 0,
        pro: 0,
        hotspots: 0,
        colorfinder: 0,
        liveVideo: 0,
    }

    for (const member of members) {
        const license: Licenses = member?.license?.subscriptionType || Licenses.Lite
        if (result[license] !== undefined) {
            result[license]++
        }
        const plugins = member.addOns
        if (plugins)
            Object.entries(plugins).forEach(([pluginName, value]) => {
                if (value) result[pluginName as AddOns]++
            })
    }

    return result
}

const licenseOrder: { [key: string]: number } = {
    pro: 1,
    essentials: 2,
    lite: 3,
}

//TODO: temp solution for distinguishing between editors and admins before implementing the backend for this
//FOR NOW: admin is the creator, everyone else is an editor
export const sortMembersInTable = (
    members: DiscriminatedUnion<WorkspaceMember>[],
    primarySortKey = "licenseOrder",
    secondarySortKey = "fullName",
    sortDirection: "asc" | "desc" = "asc"
) => {
    const usersWithLicenseOrder = members?.map((member) => ({
        ...member,
        pluginOrder: Object.values(member.addOns || {})
            .map((value) => !value)
            .join(" "),
        access: member.permissions.workspaceAccess,
        licenseOrder: licenseOrder[member.license?.subscriptionType || ""],
    }))

    return [...(usersWithLicenseOrder ?? [])].sort((a, b) => {
        const keys = [primarySortKey, secondarySortKey]
        const dirs = [sortDirection, "asc"]
        for (let i = 0; i < keys.length; i++) {
            const dir = dirs[i] === "desc" ? -1 : 1
            const aVal = (a as Record<string, unknown>)[keys[i]]
            const bVal = (b as Record<string, unknown>)[keys[i]]
            if (aVal == null && bVal == null) continue
            if (aVal == null) return 1
            if (bVal == null) return -1
            if (aVal < bVal) return -1 * dir
            if (aVal > bVal) return dir
        }
        return 0
    }) satisfies TableMember[]
}

export const flattenTagTree = (tree: Folder[], output: Record<ID, Folder>) => {
    tree.forEach((item) => {
        //if (output[item.id]) throw new Error("Found cycle while flattening tag tree.")
        // TODO handle duplicate ID, ideally delete it from dynamo
        if (!output[item.id]) {
            output[item.id] = item
            if (item.subTags) flattenTagTree(item.subTags, output)
        }
    })
}

/**
 * @param trees List of top-level trees, usually the `sidebarTree`.
 * @param id ID to find the ancestors of.
 * @returns List of ancestors ids in downward order, including the provided argument ID.
 */
export const findTagAncestorIds = (trees: Folder[], id: ID) => {
    const ancestors: ID[] = []

    const findTagAncestorIdsRecursive = (tree: Folder, id: ID, ancestors: ID[]) => {
        if (tree.id === id) {
            ancestors.push(tree.id)
            return true
        }
        if (!tree.subTags) return false

        for (const subTree of tree.subTags) {
            if (findTagAncestorIdsRecursive(subTree, id, ancestors)) {
                ancestors.push(tree.id)
                return true
            }
        }
    }

    for (const tree of trees) {
        if (findTagAncestorIdsRecursive(tree, id, ancestors)) {
            return ancestors.reverse()
        }
    }

    return []
}

/**
 * @param trees List of top-level trees, usually the `sidebarTree`.
 * @param id ID to find the ancestors of.
 * @returns List of ancestors in downward order, including the ancestor for the provided ID.
 */
export const findTagAncestors = (trees: Folder[], id?: ID) => {
    if (!id) return []
    const ancestors: Folder[] = []

    const findTagAncestorsRecursive = (tree: Folder, id: ID, ancestors: Folder[]) => {
        if (tree.id === id) {
            ancestors.push(tree)
            return true
        }
        if (!tree.subTags) return false

        for (const subTree of tree.subTags) {
            if (findTagAncestorsRecursive(subTree, id, ancestors)) {
                ancestors.push(tree)
                return true
            }
        }
    }

    for (const tree of trees) {
        if (findTagAncestorsRecursive(tree, id, ancestors)) {
            return ancestors.reverse()
        }
    }

    return []
}

type WorkspaceContextMenuSectionsProps = {
    /**
     * This is used to identify this unique instance for renaming
     * or showing the a context menu. Multiple components with the
     * same `id` can appear on the screen.
     */
    key: string
    id: ID
    title: string
    isFavorite: boolean

    onCreateFolder?: (insideOfId?: ID) => Promise<void>
    onToggleFavorite?: (id: ID) => void
    onBeginRenaming?: (id: ID) => void
    onMoveTo?: (id: ID) => void
    onOpenShareModal?: (id: ID) => void
    onDelete?: (id: ID, name: string) => void
    onOpenInNewTab?: (id: ID) => void
}

/**
 * This should only be called by the `useWorkspaceContextMenuSections`
 * hook. Permission are handled there.
 */
export const createContextMenuSections = ({
    id,
    key,
    title,
    isFavorite,
    onCreateFolder,
    onToggleFavorite,
    onBeginRenaming,
    onMoveTo,
    onOpenShareModal,
    onDelete,
    onOpenInNewTab,
}: WorkspaceContextMenuSectionsProps) => {
    const sections: ContextMenuItem[][] = []

    // Create first section.
    if (onCreateFolder)
        sections.push([
            {
                title: "New Folder",
                Icon: CreateFolder,
                onClick: () => onCreateFolder(id),
            },
        ])

    // Create second section.
    if (onToggleFavorite)
        sections.push([
            {
                title: isFavorite ? "Unfavorite" : "Favorite",
                Icon: isFavorite ? StarFill : Star,
                onClick: (e) => {
                    e.stopPropagation()
                    onToggleFavorite(id)
                },
                iconClass: isFavorite ? "text-yellow-500" : undefined,
            },
        ])

    // Create third section.
    if (onBeginRenaming || onMoveTo || onOpenShareModal)
        sections.push([
            ...((onBeginRenaming
                ? [
                      {
                          title: "Rename",
                          Icon: Pencil,
                          onClick: (e) => {
                              e.stopPropagation()
                              onBeginRenaming(key)
                          },
                      },
                  ]
                : []) as ContextMenuItem[]),
            ...((onMoveTo
                ? [
                      {
                          title: "Move To",
                          Icon: MoveTo,
                          onClick: (e) => {
                              e.stopPropagation()
                              onMoveTo(id)
                          },
                      },
                  ]
                : []) as ContextMenuItem[]),
            ...((onOpenShareModal
                ? [
                      {
                          title: "Share",
                          Icon: PersonAdd,
                          onClick: () => onOpenShareModal(id),
                      },
                  ]
                : []) as ContextMenuItem[]),
            ...((onOpenInNewTab
                ? [
                      {
                          title: "Open in new tab",
                          Icon: OpenInNew,
                          onClick: () => onOpenInNewTab(id),
                      },
                  ]
                : []) as ContextMenuItem[]),
        ])

    // Create fourth section.
    if (onDelete)
        sections.push([
            {
                title: "Delete",
                Icon: Trash,
                isDestructive: true,
                onClick: () => onDelete(id, title),
            },
        ])

    return sections
}

export const findMaxTreeDepth = (tree: Pick<WorkspaceTreeItem, "subTags">) => {
    let max = 0

    const dfs = (tree: Pick<WorkspaceTreeItem, "subTags">, depth = 0) => {
        tree.subTags?.forEach((subtree) => {
            const maxDepth = dfs(subtree, depth + 1)
            max = Math.max(max, maxDepth)
        })
        return depth
    }

    dfs(tree)
    return max
}

export const listAllSubChains = (tree: WorkspaceTreeItem): string[][] => {
    const chains: string[][] = []

    const dfs = (tree: WorkspaceTreeItem, chain: string[]) => {
        chain.push(tree.id)

        chains.push([...chain])

        tree.subTags?.forEach((subTree) => {
            dfs(subTree, chain)
        })

        chain.pop()
    }

    dfs(tree, [])

    return chains
}
